// src/pages/SubDealer/EditSubDealer.jsx
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import subDealerService from '../../services/subDealer.service';
import Toast from '../../components/Toast/Toast';
import '../../styles/AddPages.css';

const ADMIN_ROLES = ['SUPER_ADMIN', 'OFFICE_ADMIN'];

const EditSubDealer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const userRoleName = user?.role?.name || user?.role || '';
  const isAdmin = ADMIN_ROLES.includes(userRoleName);

  const [toast, setToast]             = useState(null);
  const [loading, setLoading]         = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors]           = useState({});
  const [photoPreview, setPhotoPreview] = useState(null);

  const [latLongLockedFromDB, setLatLongLockedFromDB] = useState(false);
  const [parentDealerName, setParentDealerName]       = useState('');

  const latLongLocked       = !isAdmin && latLongLockedFromDB;
  const otherFieldsDisabled = !isAdmin;

  const [formData, setFormData] = useState({
    photo: null,
    latitude: '',
    longitude: '',
    subDealerShopName: '',
    ownerName: '',
    gstNumber: '',
    panNumber: '',
    aadharCardNumber: '',
    dateOfBirth: '',
    address: '',
    state: '',
    district: '',
    tahsil: '',
    mobile: '',
    email: '',
    remark: '',
  });

  useEffect(() => {
    const init = async () => {
      try {
        const res = await subDealerService.getSubDealerById(id);
        if (res.success) {
          const d = res.data?.data || res.data;
          setLatLongLockedFromDB(!!d.latLongUpdatedByNonAdmin);
          setParentDealerName(d.dealer?.dealerShopName || '');

          setFormData({
            photo: null,
            latitude:         d.latitude         || '',
            longitude:        d.longitude        || '',
            subDealerShopName: d.subDealerShopName || '',
            ownerName:        d.ownerName        || '',
            gstNumber:        d.gstNumber        || '',
            panNumber:        d.panNumber        || '',
            aadharCardNumber: d.aadharCardNumber || '',
            dateOfBirth: (() => {
              if (!d.dateOfBirth) return '';
              const date = new Date(d.dateOfBirth);
              return isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
            })(),
            address:  d.address  || '',
            state:    d.state    || '',
            district: d.district || '',
            tahsil:   d.tahsil   || '',
            mobile:   d.mobile   || '',
            email:    d.email    || '',
            remark:   d.remark   || '',
          });
          if (d.photo) setPhotoPreview(d.photo);
        }
      } catch (e) {
        setToast({ message: 'Failed to load sub-dealer', type: 'error' });
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    if ((name === 'latitude' || name === 'longitude') && latLongLocked) return;
    if (otherFieldsDisabled && name !== 'latitude' && name !== 'longitude') return;

    if (type === 'file') {
      const file = files[0];
      setFormData(prev => ({ ...prev, [name]: file }));
      if (file) {
        const r = new FileReader();
        r.onloadend = () => setPhotoPreview(r.result);
        r.readAsDataURL(file);
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const detectLocation = () => {
  if (!navigator.geolocation) {
    setToast({ message: 'Geolocation is not supported by your browser.', type: 'error' });
    return;
  }

  setToast({ message: 'Detecting location…', type: 'info' });

  let bestReading = null;
  let settled = false;

  const finish = (position) => {
    if (settled) return;
    settled = true;
    navigator.geolocation.clearWatch(watchId);

    const { latitude, longitude, accuracy } = position.coords;
    setFormData(prev => ({
      ...prev,
      latitude: latitude.toFixed(8),
      longitude: longitude.toFixed(8),
      locationAccuracy: accuracy, // store this on the entity too
    }));

   setToast({ message: '📍 Location detected!', type: 'success' });
  };

  const watchId = navigator.geolocation.watchPosition(
    (position) => {
      if (!bestReading || position.coords.accuracy < bestReading.coords.accuracy) {
        bestReading = position;
      }
      // good enough — stop early
      if (position.coords.accuracy <= 15) {
        finish(position);
      }
    },
    (error) => {
      if (settled) return;
      settled = true;
      navigator.geolocation.clearWatch(watchId);
      const message =
        error.code === error.PERMISSION_DENIED
          ? 'Location access denied. Please enable it in your browser/app settings.'
          : error.code === error.TIMEOUT
          ? 'Location request timed out. Please try again.'
          : 'Unable to detect location. ';
      setToast({ message, type: 'error' });
      console.error('Geolocation error:', error);
    },
    { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
  );

  // Hard stop after ~12s — use best reading we've got rather than hang forever
  setTimeout(() => {
    if (!settled && bestReading) {
      finish(bestReading);
    } else if (!settled) {
      settled = true;
      navigator.geolocation.clearWatch(watchId);
      setToast({ message: 'Could not get an accurate location. ', type: 'error' });
    }
  }, 12000);
};

  const validate = () => {
    if (!isAdmin) {
      const e = {};
      if (!formData.latitude || !formData.longitude) {
        e.latitude = 'Both latitude and longitude are required';
      }
      setErrors(e);
      return Object.keys(e).length === 0;
    }
    const e = {};
    if (!formData.subDealerShopName.trim()) e.subDealerShopName = 'Required';
    if (!formData.ownerName.trim())         e.ownerName         = 'Required';
    if (!formData.mobile.trim()) {
      e.mobile = 'Required';
    } else if (!/^\d{10}$/.test(formData.mobile)) {
      e.mobile = 'Must be 10 digits';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    if (latLongLocked) {
      setToast({ message: 'GPS location is locked', type: 'error' });
      return;
    }

    setIsSubmitting(true);
    try {
      let dataToSend;
      if (isAdmin) {
        dataToSend = { ...formData };
        if (formData.photo) dataToSend.photo = photoPreview;
        else delete dataToSend.photo;
      } else {
        dataToSend = { latitude: formData.latitude, longitude: formData.longitude };
      }

      const res = await subDealerService.updateSubDealer(id, dataToSend);
      if (res.success) {
        setToast({
          message: isAdmin
            ? 'Sub-dealer updated!'
            : 'GPS location updated. It is now locked.',
          type: 'success',
        });
        setTimeout(() => navigate('/sub-dealer/view'), 1200);
      }
    } catch (err) {
      setToast({ message: err.response?.data?.message || 'Failed to update', type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <div className="add-dealer-page"><p style={{ padding: 40, textAlign: 'center' }}>Loading...</p></div>;
  }

  return (
    <div className="add-dealer-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><a href="/dashboard/overview">Dashboard</a></li>
          <li className="breadcrumb-item"><a href="/sub-dealer/view">Sub-Dealers</a></li>
          <li className="breadcrumb-item active">Edit Sub-Dealer</li>
        </ol>
      </nav>

      <div className="page-header">
        <h4 className="page-title">Edit Sub-Dealer</h4>
        {parentDealerName && (
          <p style={{ fontSize: 13, color: '#6c757d', marginTop: 4 }}>
            Parent Dealer: <strong>{parentDealerName}</strong>
          </p>
        )}
        {!isAdmin && (
          <p style={{ fontSize: 13, color: '#6c757d', marginTop: 4 }}>
            ℹ️ You can only update GPS location.
            {latLongLockedFromDB
              ? ' Location already updated once — locked.'
              : ' You can update location ONCE.'}
          </p>
        )}
      </div>

      <div className="form-card">
        <form onSubmit={handleSubmit}>

          {/* Photo & Location */}
          <div className="form-section">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Photo</label>
                <input type="file" name="photo" className="form-input"
                  onChange={handleChange} accept="image/*"
                  disabled={otherFieldsDisabled} />
                {photoPreview && (
                  <div className="photo-preview-large">
                    <img src={photoPreview} alt="Preview" />
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">
                  Latitude
                  {latLongLocked && <span style={{ color: '#c62828', fontSize: 11, marginLeft: 6 }}>🔒 Locked</span>}
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input type="text" name="latitude" className="form-input"
                    value={formData.latitude} onChange={handleChange}
                    placeholder="Latitude" style={{ flex: 1 }}
                    disabled={latLongLocked} />
                  <button type="button" className="btn btn-secondary"
                    onClick={detectLocation} disabled={latLongLocked}>📍 Detect</button>
                </div>
                {errors.latitude && <span className="error-message">{errors.latitude}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">
                  Longitude
                  {latLongLocked && <span style={{ color: '#c62828', fontSize: 11, marginLeft: 6 }}>🔒 Locked</span>}
                </label>
                <input type="text" name="longitude" className="form-input"
                  value={formData.longitude} onChange={handleChange}
                  placeholder="Longitude" disabled={latLongLocked} />
              </div>
            </div>
          </div>

          {/* Basic Info */}
          <div className="form-section">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Shop Name <span className="required">*</span></label>
                <input type="text" name="subDealerShopName"
                  className={`form-input ${errors.subDealerShopName ? 'error' : ''}`}
                  value={formData.subDealerShopName} onChange={handleChange}
                  disabled={otherFieldsDisabled} />
                {errors.subDealerShopName && <span className="error-message">{errors.subDealerShopName}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Owner Name <span className="required">*</span></label>
                <input type="text" name="ownerName"
                  className={`form-input ${errors.ownerName ? 'error' : ''}`}
                  value={formData.ownerName} onChange={handleChange}
                  disabled={otherFieldsDisabled} />
                {errors.ownerName && <span className="error-message">{errors.ownerName}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Date of Birth</label>
                <input type="date" name="dateOfBirth" className="form-input"
                  value={formData.dateOfBirth} onChange={handleChange}
                  disabled={otherFieldsDisabled} />
              </div>
            </div>
          </div>

          {/* Tax & Identity */}
          <div className="form-section">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">GST Number</label>
                <input type="text" name="gstNumber" className="form-input"
                  value={formData.gstNumber} onChange={handleChange}
                  maxLength="15" disabled={otherFieldsDisabled} />
              </div>
              <div className="form-group">
                <label className="form-label">PAN Number</label>
                <input type="text" name="panNumber" className="form-input"
                  value={formData.panNumber} onChange={handleChange}
                  maxLength="10" disabled={otherFieldsDisabled} />
              </div>
              <div className="form-group">
                <label className="form-label">Aadhar</label>
                <input type="text" name="aadharCardNumber" className="form-input"
                  value={formData.aadharCardNumber} onChange={handleChange}
                  maxLength="12" disabled={otherFieldsDisabled} />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="form-section">
            <div className="form-row">
              <div className="form-group form-group-full">
                <label className="form-label">Address</label>
                <textarea name="address" className="form-textarea"
                  value={formData.address} onChange={handleChange}
                  rows="3" disabled={otherFieldsDisabled} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">State</label>
                <input type="text" name="state" className="form-input"
                  value={formData.state} onChange={handleChange}
                  disabled={otherFieldsDisabled} />
              </div>
              <div className="form-group">
                <label className="form-label">District</label>
                <input type="text" name="district" className="form-input"
                  value={formData.district} onChange={handleChange}
                  disabled={otherFieldsDisabled} />
              </div>
              <div className="form-group">
                <label className="form-label">Tahsil</label>
                <input type="text" name="tahsil" className="form-input"
                  value={formData.tahsil} onChange={handleChange}
                  disabled={otherFieldsDisabled} />
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="form-section">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Mobile <span className="required">*</span></label>
                <input type="tel" name="mobile" maxLength="10"
                  className={`form-input ${errors.mobile ? 'error' : ''}`}
                  value={formData.mobile} onChange={handleChange}
                  disabled={otherFieldsDisabled} />
                {errors.mobile && <span className="error-message">{errors.mobile}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input type="email" name="email" className="form-input"
                  value={formData.email} onChange={handleChange}
                  disabled={otherFieldsDisabled} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group form-group-full">
                <label className="form-label">Remark</label>
                <textarea name="remark" className="form-textarea"
                  value={formData.remark} onChange={handleChange}
                  rows="3" disabled={otherFieldsDisabled} />
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary"
              onClick={() => navigate('/sub-dealer/view')} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary"
              disabled={isSubmitting || (!isAdmin && latLongLocked)}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditSubDealer;